import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { etudiantsAPI } from '../services/api';
import { departementCourant, peutModifier } from '../services/droits';

/**
 * MODULE ÉTUDIANTS — onglet 4, PARAMÉTRAGE (lot 1.1)
 * ==================================================
 * Spec     : doc/modules/etudiants.md §Onglet 4
 * Maquette : doc/maquettes/etudiants_maquette_v5.html #tab-param
 *
 * C'est cet onglet qui rend l'import possible : sans mapping, aucun code étape APOGEE
 * ne se traduit en formation, et aucune ligne du fichier de la scolarité n'entre.
 *
 * Quatre cartes, dans l'ordre de la maquette :
 *   1. les deux clés de liaison, une par source, chacune dans sa couleur ;
 *   2. le mapping des codes APOGEE : un TABLEAU et sa ligne d'ajout ;
 *   3. le mapping des colonnes SCODOC vers les champs de la base ;
 *   4. le domaine de l'e-mail institutionnel, avec sa prévisualisation vivante.
 *
 * ⚠️ La carte 2 ne suit PAS la maquette, et c'est une demande explicite de Bertrand du
 * 2026-08-15 : « il faut que je puisse supprimer et modifier les différents mappings
 * créés […] en fait il faut faire comme mapping colonne SCODOC dans l'idée ».
 * La maquette dessinait des chips groupées par formation, jolies mais où modifier et
 * retirer se cachaient dans deux icônes de dix pixels. Les deux cartes de mapping ont
 * donc désormais la MÊME forme : un tableau, une colonne Actions crayon / corbeille,
 * une ligne d'ajout en dessous. Écart E20, motivé dans la spec.
 *
 * L'accélérateur de saisie tient dans une ligne de code : après un ajout réussi, seul
 * le CODE se vide. Version, régime et formation restent, donc poser B2PCLI/221 après
 * B2ATNI/221 ne demande qu'un mot. Les 15 couples du fichier réel se saisissent sans
 * jamais rouvrir quoi que ce soit.
 *
 * Ce que la maquette apporte et que la spec ne dit pas — repris ici :
 *   · la LÉGENDE des deux vocabulaires (code monospace = scolarité, badge rose =
 *     formation de l'application). Sans elle, l'écran demande de deviner qui est qui ;
 *   · le bloc orange des codes vus à l'import et pas encore mappés ;
 *   · la prévisualisation de l'e-mail qui suit la frappe.
 *
 * Écarts assumés (détaillés dans la spec §Écarts, E11 → E20) :
 *   · la clé APOGEE est le NUMÉRO ÉTUDIANT (`Individu - Code Etudiant` → `code_etudiant`)
 *     et non l'INE comme l'annonçaient maquette et spec : mesuré au §0.4 ;
 *   · l'appariement des intitulés est INSENSIBLE à la casse et aux accents (§0.8), là où
 *     maquette et spec écrivaient « sensible à la casse » ;
 *   · chaque ligne porte SON régime : deux codes de la même formation peuvent en avoir
 *     deux différents (B2QPSI/222 apprentissage et B2QPSI/223 formation continue) ;
 *   · pas de bouton « Ajouter une formation » : les formations sont le référentiel
 *     `sous_niveaux`, créé dans Planning → Paramétrage → Configuration.
 */

/** Numéro d'exemple de la prévisualisation d'e-mail — celui de la maquette. */
const NUMERO_EXEMPLE = '20253727';

/** Un message de succès s'effface seul : il n'a rien à dire une fois lu. */
const DUREE_SUCCES_MS = 4000;

const SAISIE_VIDE = {
    id: null, code_etape: '', version_etape: '', regime_inscription: '', sous_niveau_id: '',
    label: '', parcours_code: ''   // lot E.2 (v97) : le parcours du code (MTD, QMI, OSC…)
};
/** Lot E.2 : la ligne d'ajout / de modification du mapping des GROUPES SCODOC. */
const SAISIE_SCODOC_VIDE = {
    id: null, niveau_id: '', partition: '', valeur: '', sous_niveau_id: '', regime_inscription: '', parcours_code: ''
};

/** Message d'erreur lisible : le serveur nomme toujours la cause, on la relaie telle quelle. */
function messageErreur(e, repli) {
    return e?.response?.data?.error || e?.message || repli;
}

// ═════════════════════════════════════════════════════════════════════════════
// L'ONGLET
// ═════════════════════════════════════════════════════════════════════════════
function EtudiantsParametrage({ user, config, actif }) {
    const departementId = departementCourant(user, config);
    const modifiable = peutModifier(user, departementId, 'etudiants');

    const [chargement, setChargement] = useState(true);
    const [erreurGlobale, setErreurGlobale] = useState(null);
    const [donnees, setDonnees] = useState(null);
    const [dejaCharge, setDejaCharge] = useState(false);

    // Saisies en cours, carte par carte — séparées des données du serveur pour que
    // « Annuler » revienne à l'état enregistré et non à une valeur intermédiaire.
    const [cleApogee, setCleApogee] = useState('');
    const [cleScodoc, setCleScodoc] = useState('');
    const [domaine, setDomaine] = useState('');

    // Retour d'enregistrement par carte : { type: 'succes' | 'erreur', message }
    const [statuts, setStatuts] = useState({});
    const [enregistrement, setEnregistrement] = useState(null);

    // Ligne d'ajout / de modification du mapping APOGEE. `id` non nul = on modifie une
    // ligne existante, exactement comme le crayon de la carte SCODOC.
    const [saisie, setSaisie] = useState(SAISIE_VIDE);

    const [colonneScodoc, setColonneScodoc] = useState('');
    const [champScodoc, setChampScodoc] = useState('');
    const [colonneEnEdition, setColonneEnEdition] = useState(null);

    const poserStatut = useCallback((carte, type, message) => {
        setStatuts((p) => ({ ...p, [carte]: { type, message } }));
        if (type === 'succes') {
            setTimeout(() => {
                setStatuts((p) => (p[carte]?.type === 'succes' ? { ...p, [carte]: null } : p));
            }, DUREE_SUCCES_MS);
        }
    }, []);

    const appliquer = useCallback((reponse) => {
        setDonnees(reponse);
        setCleApogee(reponse.reglages.apogee_cle_liaison || '');
        setCleScodoc(reponse.reglages.scodoc_cle_liaison || '');
        setDomaine(reponse.reglages.email_domaine_institutionnel || '');
    }, []);

    const charger = useCallback(async () => {
        if (!departementId) {
            setChargement(false);
            setErreurGlobale('Aucun département sélectionné');
            return;
        }
        setChargement(true);
        setErreurGlobale(null);
        try {
            const { data } = await etudiantsAPI.getParametrage(departementId);
            appliquer(data);
        } catch (e) {
            setErreurGlobale(messageErreur(e, 'Impossible de lire le paramétrage'));
        } finally {
            setChargement(false);
        }
    }, [departementId, appliquer]);

    // Chargé à la PREMIÈRE ouverture de l'onglet, pas au montage du module : les quatre
    // panneaux existent en permanence, et interroger le serveur pour un onglet que
    // l'utilisateur n'ouvrira peut-être jamais est un appel pour rien.
    useEffect(() => {
        if (actif && !dejaCharge) {
            setDejaCharge(true);
            charger();
        }
    }, [actif, dejaCharge, charger]);

    // ── Enregistrement d'un lot de réglages ──────────────────────────────────
    const enregistrerReglages = useCallback(async (carte, reglages) => {
        setEnregistrement(carte);
        setStatuts((p) => ({ ...p, [carte]: null }));
        try {
            const { data } = await etudiantsAPI.saveParametrage(departementId, reglages);
            setDonnees((p) => ({ ...p, reglages: data.reglages }));
            poserStatut(carte, 'succes', 'Enregistré');
            return data.reglages;
        } catch (e) {
            poserStatut(carte, 'erreur', messageErreur(e, "L'enregistrement a échoué"));
            return null;
        } finally {
            setEnregistrement(null);
        }
    }, [departementId, poserStatut]);

    // ── Mapping APOGEE : ajouter, modifier, retirer ──────────────────────────
    // Rechargement complet après chaque écriture : la file orange des codes non mappés
    // dépend de l'état des associations et doit être recalculée par le serveur.
    const rafraichir = useCallback(async () => {
        const { data } = await etudiantsAPI.getParametrage(departementId);
        appliquer(data);
    }, [departementId, appliquer]);

    const validerSaisie = useCallback(async () => {
        const ligne = {
            code_etape: saisie.code_etape.trim(),
            version_etape: saisie.version_etape.trim(),
            regime_inscription: saisie.regime_inscription,
            parcours_code: saisie.parcours_code || null
        };
        setEnregistrement('mapping');
        setStatuts((p) => ({ ...p, mapping: null }));
        try {
            if (saisie.id) {
                await etudiantsAPI.modifierMapping(saisie.id, departementId, {
                    ...ligne, sous_niveau_id: saisie.sous_niveau_id, label: saisie.label
                });
                await rafraichir();
                setSaisie(SAISIE_VIDE);
                poserStatut('mapping', 'succes',
                    `${ligne.code_etape} / ${ligne.version_etape} modifié`);
                return;
            }

            const { data } = await etudiantsAPI.creerMappings(
                departementId, saisie.sous_niveau_id, [ligne]);
            await rafraichir();

            if (data.refuses && data.refuses.length) {
                poserStatut('mapping', 'erreur', data.refuses[0].motif);
                return;
            }
            // ACCÉLÉRATEUR : seul le code se vide. Version, régime et formation restent,
            // donc poser les trois codes d'un même semestre ne demande que trois mots.
            setSaisie((p) => ({ ...p, id: null, code_etape: '' }));
            poserStatut('mapping', 'succes',
                `${ligne.code_etape} / ${ligne.version_etape} associé à ${data.formation.nom}`);
        } catch (e) {
            const refuses = e?.response?.data?.refuses;
            poserStatut('mapping', 'erreur',
                (refuses && refuses.length) ? refuses[0].motif
                                            : messageErreur(e, "L'association a échoué"));
        } finally {
            setEnregistrement(null);
        }
    }, [saisie, departementId, rafraichir, poserStatut]);

    // Retrait immédiat, sans modale de confirmation : c'est la forme de la carte SCODOC,
    // et retirer un mapping ne touche AUCUN étudiant déjà importé — le mapping ne sert
    // qu'au moment de l'import. Le bandeau nomme ce qui vient de partir.
    const retirerMapping = useCallback(async (m) => {
        setEnregistrement('mapping');
        setStatuts((p) => ({ ...p, mapping: null }));
        try {
            await etudiantsAPI.supprimerMapping(m.id, departementId);
            await rafraichir();
            if (saisie.id === m.id) setSaisie(SAISIE_VIDE);
            poserStatut('mapping', 'succes',
                `${m.code_etape} / ${m.version_etape} retiré — aucun étudiant déjà importé n'est modifié`);
        } catch (e) {
            poserStatut('mapping', 'erreur', messageErreur(e, 'Le retrait a échoué'));
        } finally {
            setEnregistrement(null);
        }
    }, [departementId, rafraichir, poserStatut, saisie]);

    // ── Lot E.2 : le mapping des GROUPES SCODOC (à droite du mapping APOGEE) ─────────────────────────────────
    const [saisieScodoc, setSaisieScodoc] = useState(SAISIE_SCODOC_VIDE);
    const validerSaisieScodoc = useCallback(async () => {
        const s = saisieScodoc;
        const corps = {
            niveau_id: s.niveau_id, partition: s.partition.trim(), valeur: s.valeur.trim(),
            sous_niveau_id: s.sous_niveau_id || null, regime_inscription: s.sous_niveau_id ? s.regime_inscription : null,
            parcours_code: s.parcours_code || null
        };
        setEnregistrement('groupes');
        setStatuts((p) => ({ ...p, groupes: null }));
        try {
            if (s.id) await etudiantsAPI.modifierMappingScodoc(s.id, departementId, corps);
            else await etudiantsAPI.creerMappingScodoc(departementId, corps);
            await rafraichir();
            // ACCÉLÉRATEUR, comme APOGEE : la formation et la partition restent, seule la valeur se vide
            setSaisieScodoc((p) => ({ ...SAISIE_SCODOC_VIDE, niveau_id: p.niveau_id, partition: p.partition }));
            poserStatut('groupes', 'succes', `« ${corps.partition ? corps.partition + ' : ' : ''}${corps.valeur} » ${s.id ? 'modifié' : 'associé'}`);
        } catch (e) {
            poserStatut('groupes', 'erreur', messageErreur(e, "L'association a échoué"));
        } finally {
            setEnregistrement(null);
        }
    }, [saisieScodoc, departementId, rafraichir, poserStatut]);
    const retirerMappingScodoc = useCallback(async (m) => {
        setEnregistrement('groupes');
        setStatuts((p) => ({ ...p, groupes: null }));
        try {
            await etudiantsAPI.supprimerMappingScodoc(m.id, departementId);
            await rafraichir();
            if (saisieScodoc.id === m.id) setSaisieScodoc(SAISIE_SCODOC_VIDE);
            poserStatut('groupes', 'succes', `« ${m.valeur} » retiré — aucun étudiant déjà importé n'est modifié`);
        } catch (e) {
            poserStatut('groupes', 'erreur', messageErreur(e, 'Le retrait a échoué'));
        } finally {
            setEnregistrement(null);
        }
    }, [departementId, rafraichir, poserStatut, saisieScodoc]);

    // ── Mapping des colonnes SCODOC ──────────────────────────────────────────
    const ecrireMappingScodoc = useCallback(async (carte) => {
        const courant = { ...(donnees?.reglages?.scodoc_colonnes_mapping || {}) };
        if (colonneEnEdition && colonneEnEdition !== colonneScodoc.trim()) delete courant[colonneEnEdition];
        courant[colonneScodoc.trim()] = champScodoc;

        const reglages = await enregistrerReglages(carte, { scodoc_colonnes_mapping: courant });
        if (reglages) {
            setColonneScodoc('');
            setChampScodoc('');
            setColonneEnEdition(null);
        }
    }, [donnees, colonneScodoc, champScodoc, colonneEnEdition, enregistrerReglages]);

    const retirerColonneScodoc = useCallback(async (colonne) => {
        const courant = { ...(donnees?.reglages?.scodoc_colonnes_mapping || {}) };
        delete courant[colonne];
        await enregistrerReglages('scodoc', { scodoc_colonnes_mapping: courant });
        if (colonneEnEdition === colonne) {
            setColonneScodoc(''); setChampScodoc(''); setColonneEnEdition(null);
        }
    }, [donnees, colonneEnEdition, enregistrerReglages]);

    // ── Les mappings, rangés par formation puis par code ─────────────────────
    // Une formation supprimée du référentiel laisse ses associations derrière elle : la
    // clé étrangère les met à NULL (ON DELETE SET NULL). Elles restent visibles en tête
    // de tableau, sans quoi elles seraient inatteignables tout en continuant d'exister.
    const lignes = useMemo(() => {
        if (!donnees) return [];
        const rang = new Map(donnees.formations.map((f, i) => [f.id, i]));
        return [...donnees.mappings].sort((a, b) => {
            const ra = rang.has(a.sous_niveau_id) ? rang.get(a.sous_niveau_id) : -1;
            const rb = rang.has(b.sous_niveau_id) ? rang.get(b.sous_niveau_id) : -1;
            if (ra !== rb) return ra - rb;
            if (a.code_etape !== b.code_etape) return a.code_etape < b.code_etape ? -1 : 1;
            return a.version_etape < b.version_etape ? -1 : 1;
        });
    }, [donnees]);

    const libelleRegime = useCallback((cle) => {
        const trouve = (donnees?.regimes || []).find((r) => r.cle === cle);
        return trouve ? trouve.libelle : cle || '—';
    }, [donnees]);

    const formationDe = useCallback((sousNiveauId) =>
        (donnees?.formations || []).find((f) => f.id === sousNiveauId) || null, [donnees]);

    // ── Rendus utilitaires ───────────────────────────────────────────────────
    const statut = (carte) => {
        const s = statuts[carte];
        if (!s) return null;
        return (
            <div className={'etu-statut ' + (s.type === 'succes' ? 'etu-statut-ok' : 'etu-statut-ko')}>
                <i className={'fa-solid ' + (s.type === 'succes' ? 'fa-circle-check' : 'fa-circle-exclamation')} />
                {' '}{s.message}
            </div>
        );
    };

    if (chargement) {
        return (
            <div className="etu-carte">
                <div className="etu-vide">
                    <i className="fa-solid fa-spinner fa-spin" />
                    <div className="etu-vide-titre">Lecture du paramétrage…</div>
                </div>
            </div>
        );
    }

    if (erreurGlobale || !donnees) {
        return (
            <div className="etu-carte">
                <div className="etu-vide">
                    <i className="fa-solid fa-triangle-exclamation" />
                    <div className="etu-vide-titre">{erreurGlobale || 'Paramétrage indisponible'}</div>
                    <p className="etu-vide-texte">
                        Le serveur a refusé la lecture du paramétrage de ce département.
                    </p>
                    <button className="etu-btn etu-btn-rose" onClick={charger} type="button">
                        <i className="fa-solid fa-rotate" /> Réessayer
                    </button>
                </div>
            </div>
        );
    }

    const mappingScodoc = donnees.reglages.scodoc_colonnes_mapping || {};
    const champsScodoc = Object.entries(donnees.champsScodoc || {});
    const nonMappes = donnees.codesNonMappes || [];
    const regimes = donnees.regimes || [];
    const saisieScodocComplete = saisieScodoc.niveau_id && saisieScodoc.valeur.trim()
        && ((saisieScodoc.sous_niveau_id && saisieScodoc.regime_inscription) || (!saisieScodoc.sous_niveau_id && saisieScodoc.parcours_code));
    // Le niveau (BUT1…) d'un groupe : le référentiel du Planning le porte par son code de niveau
    const niveauDe = (f) => ((donnees.niveaux || []).find((n) => n.code === f.niveau_code) || {}).id;
    const saisieComplete = saisie.code_etape.trim() && saisie.version_etape.trim()
                        && saisie.regime_inscription && saisie.sous_niveau_id;

    return (
        <div className="etu-param">

            {/* La topbar du module affiche l'année universitaire courante : sans ce bandeau,
                l'onglet se lit comme un paramétrage DE L'ANNÉE, et on cherche un bouton
                « reprendre N-1 » qui n'aurait rien à copier. Aucune de ces quatre données ne
                porte d'année en base — mesuré le 2026-08-15, et c'est un choix, pas un oubli :
                la mémoire de « quel code étape avait cet étudiant en 2025-2026 » vit dans
                `etudiants_inscriptions`, par étudiant et par année. Ici vit la TRADUCTION, qui
                n'a pas de millésime. */}
            <div className="etu-portee">
                <i className="fa-solid fa-infinity" />
                <div>
                    <strong>Ce paramétrage vaut pour toutes les années universitaires</strong> et
                    pour tout le département : il n'y a rien à reprendre d'une année sur l'autre.
                    Quand la scolarité change ses versions d'étape à la rentrée, les nouveaux codes
                    apparaissent d'eux-mêmes dans le bloc orange <em>« codes non mappés »</em> après
                    le premier import, et il suffit de les associer.
                </div>
            </div>

            {!modifiable && (
                <div className="etu-alerte etu-alerte-info">
                    <i className="fa-solid fa-eye" /> Vous consultez ce paramétrage en lecture
                    seule : votre profil ne permet pas de le modifier.
                </div>
            )}

            {/* ═══ CARTE 1 — CLÉS DE LIAISON ═══════════════════════════════ */}
            <div className="etu-carte">
                <div className="etu-carte-entete">
                    <i className="fa-solid fa-key" />
                    Clés de liaison imports
                    <span className="etu-entete-sous">
                        — colonne du fichier source qui identifie l'étudiant
                    </span>
                </div>
                <div className="etu-carte-corps">
                    <div className="etu-cles">

                        <div className="etu-cle etu-cle-apogee">
                            <div className="etu-cle-titre">
                                <i className="fa-solid fa-file-import" /> Import APOGEE
                            </div>
                            <label className="etu-cle-libelle" htmlFor="etu-cle-apogee">
                                Colonne du fichier APOGEE utilisée comme identifiant :
                            </label>
                            <div className="etu-cle-ligne">
                                <input id="etu-cle-apogee" type="text" value={cleApogee}
                                       onChange={(e) => setCleApogee(e.target.value)}
                                       disabled={!modifiable} maxLength={120} autoComplete="off" />
                                <span className="etu-cle-cible">
                                    &rarr; champ <code>code_etudiant</code>
                                </span>
                            </div>
                            {/* La maquette et la spec écrivaient « sensible à la casse ». Le §0.8
                                a tranché l'inverse, et c'est ce qui rend l'espace final de
                                « LIEU NAISSANCE » et le double espace de « PORTABLE  - Ligne 1 »
                                survivables. */}
                            <div className="etu-cle-aide">
                                <i className="fa-solid fa-circle-info" /> L'intitulé est comparé
                                après normalisation : casse, accents et espaces multiples n'ont pas
                                d'importance.
                            </div>
                            <button className="etu-btn etu-btn-apogee" type="button"
                                    disabled={!modifiable || enregistrement === 'apogee'
                                              || !cleApogee.trim()
                                              || cleApogee === donnees.reglages.apogee_cle_liaison}
                                    onClick={() => enregistrerReglages('apogee', { apogee_cle_liaison: cleApogee })}>
                                <i className={enregistrement === 'apogee'
                                    ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-check'} /> Enregistrer
                            </button>
                            {statut('apogee')}
                        </div>

                        <div className="etu-cle etu-cle-scodoc">
                            <div className="etu-cle-titre">
                                <i className="fa-solid fa-ranking-star" /> Import SCODOC Classements
                            </div>
                            <label className="etu-cle-libelle" htmlFor="etu-cle-scodoc">
                                Colonne du fichier SCODOC utilisée comme identifiant :
                            </label>
                            <div className="etu-cle-ligne">
                                <input id="etu-cle-scodoc" type="text" value={cleScodoc}
                                       onChange={(e) => setCleScodoc(e.target.value)}
                                       disabled={!modifiable} maxLength={120} autoComplete="off" />
                                <span className="etu-cle-cible">
                                    &rarr; champ <code>code_etudiant</code>
                                </span>
                            </div>
                            <div className="etu-cle-aide">
                                <i className="fa-solid fa-circle-info" /> Même champ cible que
                                l'import APOGEE : les deux fichiers désignent l'étudiant par son
                                numéro à 8 chiffres.
                            </div>
                            <button className="etu-btn etu-btn-scodoc" type="button"
                                    disabled={!modifiable || enregistrement === 'scodoc-cle'
                                              || !cleScodoc.trim()
                                              || cleScodoc === donnees.reglages.scodoc_cle_liaison}
                                    onClick={() => enregistrerReglages('scodoc-cle', { scodoc_cle_liaison: cleScodoc })}>
                                <i className={enregistrement === 'scodoc-cle'
                                    ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-check'} /> Enregistrer
                            </button>
                            {statut('scodoc-cle')}
                        </div>

                    </div>
                </div>
            </div>

            {/* Lot E.2 (maquette « SCODOC crée les étudiants · parcours », validée le 2026-10-28) : les DEUX mappings
                côte à côte — APOGEE à gauche, les groupes SCODOC à droite. */}
            <div className="etu-mappings-cote">
            {/* ═══ CARTE 2 — MAPPING CODES APOGEE → FORMATIONS ═════════════ */}
            <div className="etu-carte">
                <div className="etu-carte-entete">
                    <i className="fa-solid fa-link" />
                    Mapping codes APOGEE &rarr; Formations
                    <span className="etu-entete-sous">
                        — code étape + version &rarr; formation de l'application
                    </span>
                    <span className="etu-carte-compte">
                        {donnees.mappings.length} code{donnees.mappings.length > 1 ? 's' : ''}
                    </span>
                </div>
                <div className="etu-carte-corps">

                    {nonMappes.length > 0 && (
                        <div className="etu-alerte etu-alerte-warning">
                            <i className="fa-solid fa-triangle-exclamation" />
                            <span>
                                {nonMappes.length} code{nonMappes.length > 1 ? 's' : ''} APOGEE non
                                mappé{nonMappes.length > 1 ? 's' : ''} au dernier import. Les
                                étudiants correspondants n'ont pas été importés.
                            </span>
                        </div>
                    )}

                    {/* La légende de la maquette : elle nomme les deux vocabulaires que
                        l'écran fait cohabiter. Sans elle, rien ne dit lequel vient d'où. */}
                    <div className="etu-legende">
                        <div className="etu-legende-item">
                            <span className="etu-code-source">B1QLII / 111</span>
                            <span>= code APOGEE (système de la scolarité)</span>
                        </div>
                        <div className="etu-legende-item">
                            {/* Classe distincte du badge des bandeaux : celui-ci vit sur fond
                                blanc. Le même style donnerait du blanc sur blanc — relu à
                                l'image, corrigé aussitôt. */}
                            <span className="etu-badge-exemple">BUT1 FI</span>
                            <span>= formation dans votre application</span>
                        </div>
                    </div>

                    {statut('mapping')}

                    {/* Même forme que la carte SCODOC ci-dessous : un tableau, une colonne
                        Actions, une ligne d'ajout. Demande de Bertrand du 2026-08-15. */}
                    <div className="etu-table-cadre etu-table-cadre-apogee">
                        <table className="etu-table etu-table-apogee">
                            <thead>
                                <tr>
                                    <th>Code étape</th>
                                    <th>Version</th>
                                    <th>Régime d'inscription</th>
                                    <th>&rarr; Formation application</th>
                                    <th>Parcours</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lignes.map((m) => {
                                    const f = formationDe(m.sous_niveau_id);
                                    return (
                                        <tr key={m.id} className={saisie.id === m.id ? 'etu-ligne-active' : undefined}>
                                            <td className="etu-mono etu-code-cellule">{m.code_etape}</td>
                                            <td className="etu-mono etu-version-cellule">{m.version_etape}</td>
                                            <td>
                                                <span className={'etu-tag-regime etu-regime-'
                                                                 + (m.regime_inscription || 'inconnu')}>
                                                    {libelleRegime(m.regime_inscription)}
                                                </span>
                                            </td>
                                            <td>
                                                {f
                                                    ? <span className="etu-badge-exemple">{f.nom}</span>
                                                    : <span className="etu-sans-formation">
                                                          <i className="fa-solid fa-link-slash" /> Formation supprimée
                                                          — à réaffecter
                                                      </span>}
                                            </td>
                                            <td>{m.parcours_code ? <strong>{m.parcours_code}</strong> : <span className="etu-aide">— tronc commun</span>}</td>
                                            <td className="etu-actions-cellule">
                                                <button type="button" className="etu-btn-icone"
                                                        disabled={!modifiable}
                                                        aria-label={`Modifier ${m.code_etape} / ${m.version_etape}`}
                                                        onClick={() => setSaisie({
                                                            id: m.id,
                                                            code_etape: m.code_etape,
                                                            version_etape: m.version_etape,
                                                            regime_inscription: m.regime_inscription || '',
                                                            sous_niveau_id: String(m.sous_niveau_id || ''),
                                                            label: m.label || '',
                                                            parcours_code: m.parcours_code || ''
                                                        })}>
                                                    <i className="fa-solid fa-pen" />
                                                </button>
                                                <button type="button" className="etu-btn-icone etu-btn-icone-danger"
                                                        disabled={!modifiable || enregistrement === 'mapping'}
                                                        aria-label={`Retirer ${m.code_etape} / ${m.version_etape}`}
                                                        onClick={() => retirerMapping(m)}>
                                                    <i className="fa-solid fa-trash" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {!lignes.length && (
                                    <tr>
                                        <td colSpan={6} className="etu-table-vide">
                                            Aucun mapping : l'import APOGEE refuserait toutes les lignes
                                            du fichier.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ── La ligne d'ajout ─────────────────────────────────── */}
                    <form className="etu-ajout-apogee"
                          onSubmit={(e) => { e.preventDefault(); if (saisieComplete) validerSaisie(); }}>
                        <input type="text" className="etu-champ-code etu-champ-apogee"
                               placeholder="ex. B1QLII" value={saisie.code_etape}
                               onChange={(e) => setSaisie((p) => ({ ...p, code_etape: e.target.value }))}
                               disabled={!modifiable} maxLength={20} autoComplete="off"
                               aria-label="Code étape APOGEE" />
                        <input type="text" className="etu-champ-code etu-champ-apogee etu-champ-version"
                               placeholder="ex. 111" value={saisie.version_etape}
                               onChange={(e) => setSaisie((p) => ({ ...p, version_etape: e.target.value }))}
                               disabled={!modifiable} maxLength={10} autoComplete="off"
                               aria-label="Version de l'étape" />
                        <select value={saisie.regime_inscription} disabled={!modifiable}
                                aria-label="Régime d'inscription"
                                onChange={(e) => setSaisie((p) => ({ ...p, regime_inscription: e.target.value }))}>
                            <option value="">— Régime</option>
                            {regimes.map((r) => <option key={r.cle} value={r.cle}>{r.libelle}</option>)}
                        </select>
                        <span className="etu-fleche">&rarr;</span>
                        <select value={saisie.sous_niveau_id} disabled={!modifiable}
                                aria-label="Formation de votre application"
                                onChange={(e) => setSaisie((p) => ({ ...p, sous_niveau_id: e.target.value }))}>
                            <option value="">— Formation</option>
                            {donnees.formations.map((f) => (
                                <option key={f.id} value={f.id}>
                                    {f.nom}{f.niveau_nom ? ` — ${f.niveau_nom}` : ''}
                                </option>
                            ))}
                        </select>
                        {/* Lot E.2 (v97) : le parcours du code — Apogée écrit ATN / PCL / QPS, le Programme MTD / QMI / OSC */}
                        <select value={saisie.parcours_code} disabled={!modifiable} aria-label="Parcours"
                                onChange={(e) => setSaisie((p) => ({ ...p, parcours_code: e.target.value }))}>
                            <option value="">— Tronc commun</option>
                            {(donnees.parcours || []).map((pc) => <option key={pc.id} value={pc.code}>{pc.code}</option>)}
                            {saisie.parcours_code && !(donnees.parcours || []).some((pc) => pc.code === saisie.parcours_code)
                                && <option value={saisie.parcours_code}>{saisie.parcours_code}</option>}
                        </select>
                        <button type="submit" className="etu-btn etu-btn-rose"
                                disabled={!modifiable || !saisieComplete || enregistrement === 'mapping'}>
                            <i className={enregistrement === 'mapping'
                                ? 'fa-solid fa-spinner fa-spin'
                                : (saisie.id ? 'fa-solid fa-check' : 'fa-solid fa-plus')} />
                            {saisie.id ? ' Enregistrer' : ' Ajouter'}
                        </button>
                        {saisie.id && (
                            <button type="button" className="etu-btn etu-btn-neutre"
                                    onClick={() => setSaisie(SAISIE_VIDE)}>
                                Annuler
                            </button>
                        )}
                    </form>

                    {/* Le régime ne se devine pas depuis le nom de la formation : mesuré le
                        2026-08-14, deux codes de BUT2 FIA portent l'apprentissage pour l'un,
                        la formation continue pour l'autre. */}
                    <div className="etu-aide etu-aide-bloc">
                        <i className="fa-solid fa-circle-info" /> Après un ajout, <strong>seul le
                        code se vide</strong> : la version, le régime et la formation restent, donc
                        poser les trois codes d'un même semestre ne demande qu'un mot à chaque fois.
                        Le régime est <strong>par ligne</strong> et ne se déduit pas de la formation :
                        une même formation peut accueillir des apprentis et de la formation continue.
                    </div>

                    {nonMappes.length > 0 && (
                        <div className="etu-non-mappes">
                            <div className="etu-non-mappes-entete">
                                <i className="fa-solid fa-triangle-exclamation" />
                                <span>Codes APOGEE vus au dernier import et non mappés</span>
                            </div>
                            <div className="etu-non-mappes-corps">
                                {nonMappes.map((c) => (
                                    <span className="etu-chip-warning"
                                          key={c.code_etape + '/' + c.version_etape}>
                                        <i className="fa-solid fa-triangle-exclamation" />
                                        {' '}{c.code_etape} / {c.version_etape}
                                        <span className="etu-chip-compte">
                                            {c.nb_lignes} ligne{c.nb_lignes > 1 ? 's' : ''}
                                        </span>
                                        {modifiable && (
                                            <button type="button" className="etu-chip-btn etu-chip-btn-alerte"
                                                    onClick={() => setSaisie({
                                                        ...SAISIE_VIDE,
                                                        code_etape: c.code_etape,
                                                        version_etape: c.version_etape
                                                    })}>
                                                Mapper
                                            </button>
                                        )}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* La maquette met ici un bouton « Ajouter une formation ». Il n'est pas
                        repris : une formation est un `sous_niveau`, référentiel du Planning.
                        Deux endroits pour la créer feraient deux référentiels concurrents. */}
                    <div className="etu-aide etu-aide-bloc">
                        <i className="fa-solid fa-circle-info" /> Une formation qui manque à la liste
                        déroulante se crée dans <strong>Planning &gt; Paramétrage &gt; Configuration</strong>,
                        en ajoutant un groupe au niveau concerné. Elle apparaît ici aussitôt.
                        {' '}Ce département en compte <strong>{donnees.formations.length}</strong>.
                    </div>
                </div>
            </div>


            {/* ═══ CARTE 2 bis — MAPPING GROUPES SCODOC → FORMATIONS (lot E.2, v97) ════════════════════════════
                SCODOC exporte UN FICHIER PAR FORMATION sans dire laquelle ; sa colonne « groupes » porte plusieurs
                partitions (« TP FI - FIA:TP QMI FA;Parcours:QMI »). Une ligne dit, pour une VALEUR d'une PARTITION dans
                une formation (BUT1, BUT2…), le groupe, le régime et/ou le parcours. Elle s'apprend aussi à l'import. */}
            <div className="etu-carte etu-carte-scodoc-groupes">
                <div className="etu-carte-entete">
                    <i className="fa-solid fa-users-viewfinder etu-entete-scodoc" />
                    Mapping groupes SCODOC &rarr; Formations
                    <span className="etu-entete-sous">— une valeur de la colonne « groupes »</span>
                    <span className="etu-carte-compte">
                        {(donnees.scodocMappings || []).length} valeur{(donnees.scodocMappings || []).length > 1 ? 's' : ''}
                    </span>
                </div>
                <div className="etu-carte-corps">
                    {(donnees.scodocEnAttente?.valeurs || []).length > 0 && (
                        <div className="etu-alerte etu-alerte-warning">
                            <i className="fa-solid fa-triangle-exclamation" />
                            <span>
                                {donnees.scodocEnAttente.valeurs.length} valeur{donnees.scodocEnAttente.valeurs.length > 1 ? 's vues' : ' vue'} au
                                dernier import et pas encore associée{donnees.scodocEnAttente.valeurs.length > 1 ? 's' : ''} :{' '}
                                <strong>{donnees.scodocEnAttente.valeurs.slice(0, 6).map((v) => (v.partition ? v.partition + ' : ' : '') + v.valeur).join(' · ')}</strong>
                                {donnees.scodocEnAttente.valeurs.length > 6 ? ' …' : ''}
                                {' '}({donnees.scodocEnAttente.lignes} étudiant{donnees.scodocEnAttente.lignes > 1 ? 's' : ''} non importé{donnees.scodocEnAttente.lignes > 1 ? 's' : ''}).
                            </span>
                        </div>
                    )}
                    {statut('groupes')}
                    <div className="etu-table-cadre etu-table-cadre-scodoc">
                        <table className="etu-table etu-table-scodoc-groupes">
                            <thead>
                                <tr><th>Formation</th><th>Partition</th><th>Valeur SCODOC</th><th>&rarr; Groupe</th><th>Régime</th><th>Parcours</th><th>Actions</th></tr>
                            </thead>
                            <tbody>
                                {(donnees.scodocMappings || []).map((m) => {
                                    const f = formationDe(m.sous_niveau_id);
                                    return (
                                        <tr key={m.id} className={saisieScodoc.id === m.id ? 'etu-ligne-active' : undefined}>
                                            <td>{m.niveau_code}</td>
                                            <td className="etu-mono">{m.partition || '—'}</td>
                                            <td className="etu-mono">{m.valeur}</td>
                                            <td>{m.sous_niveau_id ? (f ? <span className="etu-badge-exemple">{f.nom}</span> : <span className="etu-sans-formation"><i className="fa-solid fa-link-slash" /> supprimé</span>) : <span className="etu-aide">—</span>}</td>
                                            <td>{m.regime_inscription
                                                ? <span className={'etu-tag-regime etu-regime-' + m.regime_inscription}>{libelleRegime(m.regime_inscription)}</span>
                                                : <span className="etu-aide">—</span>}</td>
                                            <td>{m.parcours_code ? <strong>{m.parcours_code}</strong> : <span className="etu-aide">—</span>}</td>
                                            <td className="etu-actions-cellule">
                                                <button type="button" className="etu-btn-icone" disabled={!modifiable}
                                                        aria-label={`Modifier ${m.valeur}`}
                                                        onClick={() => setSaisieScodoc({
                                                            id: m.id, niveau_id: String(m.niveau_id), partition: m.partition || '', valeur: m.valeur,
                                                            sous_niveau_id: String(m.sous_niveau_id || ''), regime_inscription: m.regime_inscription || '',
                                                            parcours_code: m.parcours_code || ''
                                                        })}>
                                                    <i className="fa-solid fa-pen" />
                                                </button>
                                                <button type="button" className="etu-btn-icone etu-btn-icone-danger"
                                                        disabled={!modifiable || enregistrement === 'groupes'}
                                                        aria-label={`Retirer ${m.valeur}`} onClick={() => retirerMappingScodoc(m)}>
                                                    <i className="fa-solid fa-trash" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {!(donnees.scodocMappings || []).length && (
                                    <tr><td colSpan={7} className="etu-table-vide">
                                        Aucune valeur associée : l'import SCODOC des étudiants mettrait toutes les lignes en attente.
                                        Les valeurs s'associent aussi à l'import, sans quitter l'écran.
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ── La ligne d'ajout ── */}
                    <form className="etu-ajout-apogee etu-ajout-scodoc"
                          onSubmit={(e) => { e.preventDefault(); if (saisieScodocComplete) validerSaisieScodoc(); }}>
                        <select value={saisieScodoc.niveau_id} disabled={!modifiable} aria-label="Formation (niveau)"
                                onChange={(e) => setSaisieScodoc((p) => ({ ...p, niveau_id: e.target.value, sous_niveau_id: '' }))}>
                            <option value="">— Formation</option>
                            {(donnees.niveaux || []).map((n) => <option key={n.id} value={n.id}>{n.code}</option>)}
                        </select>
                        <input type="text" className="etu-champ-code" placeholder="partition, ex. TP FI - FIA" value={saisieScodoc.partition}
                               maxLength={100} disabled={!modifiable} aria-label="Partition SCODOC" autoComplete="off"
                               onChange={(e) => setSaisieScodoc((p) => ({ ...p, partition: e.target.value }))} />
                        <input type="text" className="etu-champ-code" placeholder="valeur, ex. TP QMI FA" value={saisieScodoc.valeur}
                               maxLength={100} disabled={!modifiable} aria-label="Valeur SCODOC" autoComplete="off"
                               onChange={(e) => setSaisieScodoc((p) => ({ ...p, valeur: e.target.value }))} />
                        <span className="etu-fleche">&rarr;</span>
                        <select value={saisieScodoc.sous_niveau_id} disabled={!modifiable} aria-label="Groupe de votre application"
                                onChange={(e) => setSaisieScodoc((p) => ({ ...p, sous_niveau_id: e.target.value }))}>
                            <option value="">— Groupe (facultatif)</option>
                            {donnees.formations.filter((f) => !saisieScodoc.niveau_id || String(niveauDe(f)) === saisieScodoc.niveau_id)
                                .map((f) => <option key={f.id} value={f.id}>{f.nom}</option>)}
                        </select>
                        <select value={saisieScodoc.regime_inscription} disabled={!modifiable || !saisieScodoc.sous_niveau_id} aria-label="Régime"
                                onChange={(e) => setSaisieScodoc((p) => ({ ...p, regime_inscription: e.target.value }))}>
                            <option value="">— Régime</option>
                            {regimes.map((r) => <option key={r.cle} value={r.cle}>{r.libelle}</option>)}
                        </select>
                        <select value={saisieScodoc.parcours_code} disabled={!modifiable} aria-label="Parcours"
                                onChange={(e) => setSaisieScodoc((p) => ({ ...p, parcours_code: e.target.value }))}>
                            <option value="">— Parcours</option>
                            {(donnees.parcours || []).map((pc) => <option key={pc.id} value={pc.code}>{pc.code}</option>)}
                            {saisieScodoc.parcours_code && !(donnees.parcours || []).some((pc) => pc.code === saisieScodoc.parcours_code)
                                && <option value={saisieScodoc.parcours_code}>{saisieScodoc.parcours_code}</option>}
                        </select>
                        <button type="submit" className="etu-btn etu-btn-scodoc"
                                disabled={!modifiable || !saisieScodocComplete || enregistrement === 'groupes'}>
                            <i className={enregistrement === 'groupes' ? 'fa-solid fa-spinner fa-spin' : (saisieScodoc.id ? 'fa-solid fa-check' : 'fa-solid fa-plus')} />
                            {saisieScodoc.id ? ' Enregistrer' : ' Ajouter'}
                        </button>
                        {saisieScodoc.id && (
                            <button type="button" className="etu-btn etu-btn-neutre" onClick={() => setSaisieScodoc(SAISIE_SCODOC_VIDE)}>Annuler</button>
                        )}
                    </form>
                    <div className="etu-aide etu-aide-bloc">
                        <i className="fa-solid fa-circle-info" /> Une ligne du fichier prend son <strong>groupe et son régime</strong> de
                        la valeur qui les donne, son <strong>parcours</strong> de la même ou d'une autre (ex. « Parcours : QMI »). Le
                        parcours se retrouve chaque année par son <strong>code</strong> dans le Programme
                        (Programme formation → Programme national → Semestres).
                        {!(donnees.parcours || []).length && <> <strong>Aucun parcours cette année dans le Programme.</strong></>}
                    </div>
                </div>
            </div>
            </div>

            {/* ═══ CARTE 3 — MAPPING COLONNES SCODOC ══════════════════════ */}
            <div className="etu-carte">
                <div className="etu-carte-entete">
                    <i className="fa-solid fa-ranking-star etu-entete-scodoc" />
                    Mapping colonnes SCODOC &rarr; Champs application
                    <span className="etu-entete-sous">
                        — quel en-tête du fichier alimente quel champ de la base
                    </span>
                </div>
                <div className="etu-carte-corps">
                    {statut('scodoc')}

                    <div className="etu-table-cadre etu-table-cadre-scodoc">
                        <table className="etu-table etu-table-scodoc">
                            <thead>
                                <tr>
                                    <th>Colonne fichier SCODOC</th>
                                    <th>&rarr; Champ application</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(mappingScodoc).map(([colonne, champ]) => (
                                    <tr key={colonne}
                                        className={colonneEnEdition === colonne ? 'etu-ligne-active' : undefined}>
                                        <td className="etu-mono">{colonne}</td>
                                        <td><span className="etu-badge-champ">{champ}</span></td>
                                        <td className="etu-actions-cellule">
                                            <button type="button" className="etu-btn-icone"
                                                    disabled={!modifiable}
                                                    aria-label={`Modifier la correspondance ${colonne}`}
                                                    onClick={() => {
                                                        setColonneScodoc(colonne);
                                                        setChampScodoc(champ);
                                                        setColonneEnEdition(colonne);
                                                    }}>
                                                <i className="fa-solid fa-pen" />
                                            </button>
                                            <button type="button" className="etu-btn-icone etu-btn-icone-danger"
                                                    disabled={!modifiable || enregistrement === 'scodoc'}
                                                    aria-label={`Retirer la correspondance ${colonne}`}
                                                    onClick={() => retirerColonneScodoc(colonne)}>
                                                <i className="fa-solid fa-trash" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {!Object.keys(mappingScodoc).length && (
                                    <tr>
                                        <td colSpan={3} className="etu-table-vide">
                                            Aucune correspondance : l'import SCODOC n'écrirait rien.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="etu-ajout-scodoc">
                        <input type="text" className="etu-champ-code"
                               placeholder="Nom colonne SCODOC" value={colonneScodoc}
                               onChange={(e) => setColonneScodoc(e.target.value)}
                               disabled={!modifiable} maxLength={120} autoComplete="off" />
                        <span className="etu-fleche">&rarr;</span>
                        <select value={champScodoc} onChange={(e) => setChampScodoc(e.target.value)}
                                disabled={!modifiable}>
                            <option value="">— Champ app cible</option>
                            {champsScodoc.map(([cle]) => (
                                <option key={cle} value={cle}>{cle}</option>
                            ))}
                        </select>
                        <button type="button" className="etu-btn etu-btn-scodoc"
                                disabled={!modifiable || !colonneScodoc.trim() || !champScodoc
                                          || enregistrement === 'scodoc'}
                                onClick={() => ecrireMappingScodoc('scodoc')}>
                            <i className={enregistrement === 'scodoc'
                                ? 'fa-solid fa-spinner fa-spin'
                                : (colonneEnEdition ? 'fa-solid fa-check' : 'fa-solid fa-plus')} />
                            {colonneEnEdition ? ' Enregistrer' : ' Ajouter'}
                        </button>
                        {colonneEnEdition && (
                            <button type="button" className="etu-btn etu-btn-neutre"
                                    onClick={() => {
                                        setColonneScodoc(''); setChampScodoc(''); setColonneEnEdition(null);
                                    }}>
                                Annuler
                            </button>
                        )}
                    </div>

                    {/* La liste des champs cibles est volontairement courte : SCODOC est un
                        export de RECRUTEMENT, il n'apporte que le classement à l'admission
                        (§0.5). Le dire à l'écran évite qu'on cherche les autres. */}
                    <div className="etu-aide etu-aide-bloc">
                        <i className="fa-solid fa-circle-info" /> Un seul champ est proposé, et
                        c'est mesuré : SCODOC est un export de recrutement. Il n'apporte que le
                        classement à l'admission — l'identité, l'inscription et le statut boursier
                        viennent d'APOGEE, source unique. Le rang dans la promotion inscrite
                        (<code>classement_admission</code>) est un autre nombre : sur les 48 lignes
                        qui portent les deux, 47 diffèrent.
                        {champsScodoc.map(([cle, libelle]) => (
                            <div className="etu-aide-champ" key={cle}>
                                <code>{cle}</code> — {libelle}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ═══ CARTE 4 — DOMAINE E-MAIL INSTITUTIONNEL ════════════════ */}
            <div className="etu-carte">
                <div className="etu-carte-entete">
                    <i className="fa-solid fa-at" />
                    Domaine e-mail institutionnel
                    <span className="etu-entete-sous">— sert à générer l'e-mail de chaque étudiant</span>
                </div>
                <div className="etu-carte-corps">
                    <div className="etu-alerte etu-alerte-info">
                        <i className="fa-solid fa-circle-info" />
                        <span>
                            L'e-mail institutionnel est calculé à l'import :
                            {' '}<strong>numéro étudiant @ domaine</strong>. Changer le domaine ici
                            le recalcule au prochain import.
                        </span>
                    </div>
                    <div className="etu-email">
                        <span className="etu-email-prefixe">{NUMERO_EXEMPLE}</span>
                        <span className="etu-email-at">@</span>
                        <input type="text" className="etu-email-domaine" value={domaine}
                               onChange={(e) => setDomaine(e.target.value)}
                               disabled={!modifiable} maxLength={255} autoComplete="off"
                               aria-label="Domaine de l'e-mail institutionnel" />
                    </div>
                    <div className="etu-email-apercu">
                        Exemple généré : <span>{NUMERO_EXEMPLE}@{domaine || '…'}</span>
                    </div>
                    <button className="etu-btn etu-btn-rose" type="button"
                            disabled={!modifiable || enregistrement === 'email' || !domaine.trim()
                                      || domaine === donnees.reglages.email_domaine_institutionnel}
                            onClick={() => enregistrerReglages('email', { email_domaine_institutionnel: domaine })}>
                        <i className={enregistrement === 'email'
                            ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-check'} /> Enregistrer
                    </button>
                    {statut('email')}
                </div>
            </div>
        </div>
    );
}

export default EtudiantsParametrage;
